import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@shared/ui';
import { useSaveProject } from '../use-save-project';

interface DeleteProjectDialogProps {
  open: boolean;
  projectId: string;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

export function DeleteProjectDialog({
  open,
  projectId,
  onOpenChange,
  onDeleted,
}: DeleteProjectDialogProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { deleteProject, isDeleting } = useSaveProject();

  const handleOpenChange = (next: boolean) => {
    if (isDeleting) return;
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    const result = await deleteProject(projectId);
    if (result.success) {
      toast(t('projects.form.removeSuccess'), 'success');
      onOpenChange(false);
      onDeleted?.();
      return;
    }

    if (result.error) {
      toast(result.error, 'error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('projects.form.removeConfirmTitle')}</DialogTitle>
          <DialogDescription>{t('projects.form.removeConfirmDescription')}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={isDeleting} onClick={() => handleOpenChange(false)}>
            {t('projects.form.removeConfirmCancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => void handleConfirm()}
          >
            {t('projects.form.removeConfirmAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
